import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Download, X, Send, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { FilterBar } from "@/components/shared/FilterBar";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";

interface ApiMessage {
  id: number;
  waba_message_id: string | null;
  direction: string;
  recipient_phone: string;
  message_type: string;
  content: Record<string, unknown>;
  status: string;
  source: string;
  error_code: string | null;
  error_message: string | null;
  cost: number;
  channel_name: string;
  created_at: string;
}

interface Channel {
  id: number;
  display_phone: string;
  name: string | null;
  verified_name: string | null;
}

interface Template {
  id: number;
  name: string;
  language: string;
  category: string;
  status: string;
  variable_count: number;
  body_text: string | null;
}

function SendMessageDialog({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const [mode, setMode] = useState<"template" | "text">("template");
  const [channelId, setChannelId] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [paramValues, setParamValues] = useState<string[]>([]);

  const { data: channelsData } = useQuery({
    queryKey: ["channels"],
    queryFn: () => apiFetch<{ channels: Channel[] }>("/api/channels"),
  });

  const { data: templatesData } = useQuery({
    queryKey: ["templates"],
    queryFn: () => apiFetch<{ templates: Template[] }>("/api/templates"),
  });

  const channels = channelsData?.channels ?? [];
  const templates = (templatesData?.templates ?? []).filter((t) => t.status === "approved");

  const selectedTemplate = templates.find((t) => String(t.id) === templateId);

  const sendMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        channel_id: Number(channelId),
        to,
      };
      if (mode === "template") {
        payload.type = "template";
        payload.template_id = Number(templateId);
        if (selectedTemplate && selectedTemplate.variable_count > 0) {
          payload.components = [
            {
              type: "body",
              parameters: paramValues.slice(0, selectedTemplate.variable_count).map((v) => ({
                type: "text",
                text: v || "",
              })),
            },
          ];
        }
      } else {
        payload.type = "text";
        payload.body = body;
      }
      return apiFetch("/api/messages", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      onSent();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message);
    },
  });

  const paramsReady = !selectedTemplate || selectedTemplate.variable_count === 0 || paramValues.slice(0, selectedTemplate.variable_count).every((v) => v.trim());
  const canSend = channelId && to && (mode === "template" ? templateId && paramsReady : body.trim());

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div className="absolute inset-0 bg-foreground/20" />
      <div onClick={(e) => e.stopPropagation()} className="absolute left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-sm font-semibold">Send message</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-4 p-5">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Channel</label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Select a channel...</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>{ch.name || ch.verified_name || ch.display_phone}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Recipient phone</label>
            <Input
              placeholder="e.g. 51987654321"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
            <p className="mt-1 text-[10px] text-muted-foreground">Country code + number, no + or spaces</p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Message type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode("template")}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${mode === "template" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/40"}`}
              >
                Template
              </button>
              <button
                onClick={() => setMode("text")}
                className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${mode === "text" ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-muted/40"}`}
              >
                Text (24h window)
              </button>
            </div>
          </div>

          {mode === "template" ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">Template</label>
                <select
                  value={templateId}
                  onChange={(e) => {
                    setTemplateId(e.target.value);
                    const t = templates.find((t) => String(t.id) === e.target.value);
                    setParamValues(new Array(t?.variable_count || 0).fill(""));
                  }}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select a template...</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.language})</option>
                  ))}
                </select>
                {templates.length === 0 && (
                  <p className="mt-1 text-[10px] text-warning">No approved templates. Go to Templates and sync from Meta first.</p>
                )}
              </div>
              {selectedTemplate?.body_text && (
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                  {selectedTemplate.body_text}
                </div>
              )}
              {selectedTemplate && selectedTemplate.variable_count > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-muted-foreground">
                    Template variables ({selectedTemplate.variable_count})
                  </label>
                  {Array.from({ length: selectedTemplate.variable_count }, (_, i) => (
                    <Input
                      key={i}
                      placeholder={`Value for {{${i + 1}}}`}
                      value={paramValues[i] || ""}
                      onChange={(e) => {
                        const next = [...paramValues];
                        next[i] = e.target.value;
                        setParamValues(next);
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Message body</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your message..."
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm resize-none"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">Only works if the recipient messaged you in the last 24 hours.</p>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <Button
            className="w-full"
            disabled={!canSend || sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send message
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Messages() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSend, setShowSend] = useState(false);

  const { data } = useQuery({
    queryKey: ["messages"],
    queryFn: () => apiFetch<{ messages: ApiMessage[]; total: number }>("/api/messages"),
    refetchInterval: 5000,
  });

  const hasReal = data && data.messages.length > 0;

  const displayMessages = hasReal
    ? data.messages.map((m) => ({
        id: String(m.id),
        timestamp: m.created_at,
        status: m.status as any,
        direction: m.direction === "outbound" ? "OUT" : "IN",
        recipient: m.recipient_phone,
        channel: m.channel_name,
        source: m.source,
        content: extractContent(m),
      }))
    : [];

  const selected = displayMessages.find((m) => m.id === selectedId) ?? null;
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        description="Per-message log across all channels and sources."
        actions={
          <Button size="sm" onClick={() => setShowSend(true)}>
            <Send className="h-4 w-4" />Send message
          </Button>
        }
      />

      {data && !hasReal && (
        <div className="rounded-lg border border-muted bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
          No messages yet. Send your first message to get started.
        </div>
      )}

      <FilterBar searchPlaceholder="Search by phone or message ID..." />

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Timestamp</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Direction</th>
                <th className="px-4 py-3 text-left font-medium">Recipient</th>
                <th className="px-4 py-3 text-left font-medium">Channel</th>
                <th className="px-4 py-3 text-left font-medium">Source</th>
                <th className="px-4 py-3 text-left font-medium">Content</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {displayMessages.map((m) => (
                <tr key={m.id} onClick={() => setSelectedId(m.id)} className="cursor-pointer hover:bg-muted/40">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{new Date(m.timestamp).toLocaleString()}</td>
                  <td className="px-4 py-3"><StatusBadge status={m.status} /></td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${m.direction === "OUT" ? "text-primary" : "text-info"}`}>
                      {m.direction === "OUT" ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownLeft className="h-3.5 w-3.5" />}
                      {m.direction}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 font-mono text-xs">{m.recipient}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs">{m.channel}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{m.source}</td>
                  <td className="max-w-[320px] truncate px-4 py-3 text-xs text-muted-foreground">{m.content}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-border px-4 py-3 text-xs text-muted-foreground">
          <span>Showing {displayMessages.length} of {total.toLocaleString()} messages</span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm">Previous</Button>
            <Button variant="outline" size="sm">Next</Button>
          </div>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-40" onClick={() => setSelectedId(null)}>
          <div className="absolute inset-0 bg-foreground/20" />
          <aside onClick={(e) => e.stopPropagation()} className="absolute right-0 top-0 h-full w-full max-w-md overflow-y-auto border-l border-border bg-card shadow-xl">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h3 className="text-sm font-semibold">Message detail</h3>
              <Button variant="ghost" size="icon" onClick={() => setSelectedId(null)} aria-label="Close"><X className="h-4 w-4" /></Button>
            </div>
            <div className="space-y-5 p-5 text-sm">
              <div className="flex items-center gap-2"><StatusBadge status={selected.status} /><span className="text-xs text-muted-foreground">{selected.direction}</span></div>
              <dl className="grid grid-cols-3 gap-y-2 text-xs">
                <dt className="col-span-1 text-muted-foreground">Message ID</dt><dd className="col-span-2 font-mono">{selected.id}</dd>
                <dt className="text-muted-foreground">Recipient</dt><dd className="col-span-2 font-mono">{selected.recipient}</dd>
                <dt className="text-muted-foreground">Channel</dt><dd className="col-span-2">{selected.channel}</dd>
                <dt className="text-muted-foreground">Source</dt><dd className="col-span-2">{selected.source}</dd>
                <dt className="text-muted-foreground">Timestamp</dt><dd className="col-span-2">{new Date(selected.timestamp).toLocaleString()}</dd>
              </dl>
              <div>
                <div className="mb-2 text-xs font-medium text-muted-foreground">Content</div>
                <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">{selected.content}</div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {showSend && (
        <SendMessageDialog
          onClose={() => setShowSend(false)}
          onSent={() => queryClient.invalidateQueries({ queryKey: ["messages"] })}
        />
      )}
    </div>
  );
}

function extractContent(m: ApiMessage): string {
  if (m.content?.body) return String(m.content.body);
  if (m.content?.text && typeof m.content.text === "object") return String((m.content.text as any).body || "");
  if (m.content?.template_name) return `[Template: ${m.content.template_name}]`;
  if (m.content?.caption) return String(m.content.caption);
  return `[${m.message_type}]`;
}
